/* AI USAGE — HEVN Admin (Jay task #1179, 03/11). What the portal's AI costs, moved out of AI Hub settings because it covers every AI
   feature: today against the people's cap (coloured at 50 / 80 %, with the time left to the Vietnam midnight reset) and the SYSTEM
   jobs' own budget; each person today against their own cap and their Deeper analysis runs; this month against last month; the daily
   spend with the cap as a line; the period's rows filtered and grouped by person, entity, department, feature, model or day.
   One person is one ACCOUNT (the server groups by user_id); entity and department come from the account; GCC test rooms are left out.
   Settings, collapsed: the caps, the effort each feature runs at (Jay #1181, Max 03/11: choose model and effort per job), the model
   prices, which API keys are set. Admin only — the server checks the Admin role as well as the grant. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Legend } from 'recharts';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import './style.css';
import './ProjectApproval.css';
import './SPHub.css';
import './AiUsage.css';

const API = `${BACKEND_URL}/api/admin/ai-usage`;
const errOf = (e) => e?.response?.data?.error || e.message;
const FAMS = ['haiku', 'sonnet', 'opus'];
const TOUR = [
  { selector: '[data-au-today]', title: 'xAiUse.tourTodayT', body: 'xAiUse.tourTodayB' },
  { selector: '[data-au-people]', title: 'xAiUse.tourPeopleT', body: 'xAiUse.tourPeopleB' },
  { selector: '[data-au-filters]', title: 'xAiUse.tourFilterT', body: 'xAiUse.tourFilterB' },
  { selector: '[data-au-settings]', title: 'xAiUse.tourSettingsT', body: 'xAiUse.tourSettingsB' },
];

// the Vietnam day as yyyy-mm-dd, and the periods built on it
const vnDay = (d = new Date()) => new Date(d.getTime() + 7 * 3600000).toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const periodOf = (p) => {
  const today = vnDay();
  if (p === 'today') return [today, today];
  if (p === 'week') { const dow = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7; return [addDays(today, -dow), today]; }
  if (p === 'last') { const first = `${today.slice(0, 8)}01`; const end = addDays(first, -1); return [`${end.slice(0, 8)}01`, end]; }
  return [`${today.slice(0, 8)}01`, today];
};
const usd = (v, d = 2) => `USD ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const int = (v) => Number(v || 0).toLocaleString('en-US');
const pct = (a, b) => (b > 0 ? (a / b) * 100 : 0);
const level = (p) => (p >= 80 ? 'red' : p >= 50 ? 'amber' : 'green');
// 'claude-haiku-5-5' → 'Haiku 5.5'
const modelName = (m) => {
  const x = String(m || '').match(/^claude-([a-z]+)-(\d+)(?:-(\d+))?/);
  return x ? `${x[1][0].toUpperCase()}${x[1].slice(1)} ${x[2]}${x[3] ? `.${x[3]}` : ''}` : (m || '');
};
const dmy = (iso) => { const s = String(iso || '').slice(0, 10); const [y, m, d] = s.split('-'); return y ? `${d}/${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1]}` : ''; };

function Meter({ label, spent, cap, testId }) {
  const { t } = useTranslation();
  const p = pct(spent, cap);
  return (
    <div className="au-meter" data-au-meter={testId}>
      <div className="au-meter-head"><span>{label}</span><b>{t('xAiUse.capLine', { spent: Number(spent).toFixed(2), cap: Number(cap).toFixed(2), pct: Math.round(p) })}</b></div>
      <div className="au-bar">
        <div className={`au-fill ${level(p)}`} style={{ width: `${Math.min(100, p)}%` }} />
        <i className="au-tick" style={{ left: '50%' }} /><i className="au-tick" style={{ left: '80%' }} />
      </div>
    </div>
  );
}

/* ONE PERSON'S DAILY CAP (Jay on #1179, 03/11: "Please allow to adjust daily budget per users"): their own cap, used every day
   instead of the common one (empty = the common one), and an extra for today only. Drawn into <body> on its own layer, so no
   page box can sit over its buttons. */
function CapDialog({ who, data, onClose, onSaved }) {
  const { t } = useTranslation();
  const own = data.personCaps?.own?.[who.id];
  const extra = data.personCaps?.extra?.[who.id];
  const [cap, setCap] = useState(own ?? '');
  const [plus, setPlus] = useState(extra || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { const h = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);
  const save = () => {
    setBusy(true); setErr('');
    axios.put(`${API}/user-cap`, { userId: who.id, cap: cap === '' ? null : Number(cap), extraToday: plus === '' ? null : Number(plus) })
      .then(() => onSaved()).catch((e) => { setErr(errOf(e)); setBusy(false); });
  };
  return createPortal(
    <div className="au-dlg-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="au-dlg" role="dialog" aria-modal="true" data-au-capdlg>
        <h3>{t('xAiUse.capDlgTitle', { name: who.name })}</h3>
        <label><span>{t('xAiUse.capOwn')}</span>
          <input type="number" min="0.5" step="0.5" value={cap} onChange={(e) => setCap(e.target.value)} placeholder={Number(data.caps.person).toFixed(2)} data-au-capown autoFocus />
          <small>{t('xAiUse.capOwnHint', { v: Number(data.caps.person).toFixed(2) })}</small>
        </label>
        <label><span>{t('xAiUse.capExtra')}</span>
          <input type="number" min="0" step="0.5" value={plus} onChange={(e) => setPlus(e.target.value)} placeholder="0" data-au-capextra />
          <small>{t('xAiUse.capExtraHint')}</small>
        </label>
        {err && <p className="sp-error">{err}</p>}
        <div className="au-dlg-foot">
          <button type="button" className="sp-btn" onClick={onClose}>{t('xAiUse.cancel')}</button>
          <button type="button" className="sp-btn sp-btn-primary" onClick={save} disabled={busy} data-au-capdlgsave>{t('xAiUse.save')}</button>
        </div>
      </div>
    </div>, document.body);
}

function Settings({ data, reload, onCap }) {
  const { t } = useTranslation();
  const [caps, setCaps] = useState(data.caps);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [prices, setPrices] = useState(data.prices);
  const [own, setOwn] = useState(data.efforts.own || {});
  useEffect(() => { setCaps(data.caps); setPrices(data.prices); setOwn(data.efforts.own || {}); }, [data]);
  const flash = (m) => { setMsg(m); setErr(''); setTimeout(() => setMsg(''), 4000); };
  const saveCaps = () => axios.put(`${API}/caps`, caps).then(() => { flash(t('xAiUse.saved')); reload(); }).catch((e) => setErr(errOf(e)));
  const savePrice = (p) => axios.put(`${API}/prices/${encodeURIComponent(p.model)}`, p).then(() => { flash(t('xAiUse.saved')); reload(); }).catch((e) => setErr(errOf(e)));
  const setEffort = (feature, family, lv) => axios.put(`${API}/effort`, { feature, family, level: lv })
    .then((r) => { setOwn(r.data.own || {}); flash(t('xAiUse.eSaved')); }).catch((e) => setErr(errOf(e)));
  const { routes, modelDefault, levels } = data.efforts;
  // every feature the code routes, and any other the period used
  const used = useMemo(() => {
    const m = {};
    data.rows.forEach((r) => { m[r.feature] = m[r.feature] || new Set(); m[r.feature].add(String(r.model).match(/^claude-([a-z]+)-/)?.[1]); });
    return m;
  }, [data.rows]);
  const features = useMemo(() => [...new Set([...Object.keys(routes), ...Object.keys(used)])].filter((f) => !f.startsWith('test.')).sort(), [routes, used]);
  const featName = (f) => t(`xAiUse.feat_${f.replace(/\./g, '_')}`, { defaultValue: f });
  const lvName = (l) => t(`xAiUse.lv_${l}`, { defaultValue: l });
  return (
    <details className="au-settings" data-au-settings>
      <summary>{t('xAiUse.settings')}</summary>
      {msg && <p className="au-ok">{msg}</p>}
      {err && <p className="sp-error">{err}</p>}

      <h4>{t('xAiUse.capsTitle')}</h4>
      <div className="au-caps">
        {[['users', 'capUsers'], ['person', 'capPerson'], ['system', 'capSystem'], ['opus', 'capOpus']].map(([k, label]) => (
          <label key={k}><span>{t(`xAiUse.${label}`)}</span>
            <input type="number" min="0" step={k === 'opus' ? 1 : 0.5} value={caps[k] ?? ''} onChange={(e) => setCaps({ ...caps, [k]: e.target.value })} data-au-cap={k} />
          </label>
        ))}
        <button type="button" className="sp-btn sp-btn-primary" onClick={saveCaps} data-au-capsave>{t('xAiUse.save')}</button>
      </div>

      <h4>{t('xAiUse.personCapsTitle')}</h4>
      <p className="au-hint">{t('xAiUse.personCapsHint')}</p>
      {(() => {
        const ids = [...new Set([...Object.keys(data.personCaps?.own || {}), ...Object.keys(data.personCaps?.extra || {})])].map(Number);
        const acct = (id) => data.accounts.find((a) => a.id === id) || { id, name: `#${id}` };
        return (
          <>
            {!ids.length ? <p className="sp-sub">{t('xAiUse.nonePersonal')}</p> : (
              <div className="au-scroll">
                <table className="sp-table au-table au-pcaps" data-au-pcaps>
                  <thead><tr><th>{t('xAiUse.colPerson')}</th><th className="num">{t('xAiUse.colCapOwn')}</th><th className="num">{t('xAiUse.colExtra')}</th><th>{t('xAiUse.colAction')}</th></tr></thead>
                  <tbody>
                    {ids.map((id) => {
                      const a = acct(id); const own = data.personCaps.own?.[id]; const ex = data.personCaps.extra?.[id];
                      return (
                        <tr key={id} data-au-pcap={id}>
                          <td>{a.name}{a.entity ? <small className="au-code">{a.entity}</small> : null}</td>
                          <td className="num">{own ? usd(own) : `— (${usd(data.caps.person)})`}</td>
                          <td className="num">{ex ? usd(ex) : '—'}</td>
                          <td className="au-acts">
                            <button type="button" className="sp-btn au-act-edit" onClick={() => onCap(a)}>{t('xAiUse.edit')}</button>
                            {(own || ex) && <button type="button" className="sp-btn au-act-reset" data-au-pcapreset={id}
                              onClick={() => axios.put(`${API}/user-cap`, { userId: id, cap: null, extraToday: null }).then(reload).catch((e) => setErr(errOf(e)))}>{t('xAiUse.toCommon')}</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <select value="" onChange={(e) => { const a = acct(Number(e.target.value)); if (e.target.value) onCap(a); }} className="au-addperson" data-au-addperson>
              <option value="">{t('xAiUse.addPerson')}</option>
              {data.accounts.filter((a) => !ids.includes(a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}{a.entity ? ` · ${a.entity}` : ''}</option>)}
            </select>
          </>
        );
      })()}

      <h4>{t('xAiUse.effortTitle')}</h4>
      <p className="au-hint">{t('xAiUse.effortHint')}</p>
      <div className="au-scroll">
        <table className="sp-table au-table au-effort" data-au-effort>
          <thead><tr><th>{t('xAiUse.eFeature')}</th><th>{t('xAiUse.eUsed')}</th>{FAMS.map((f) => <th key={f}>{f[0].toUpperCase() + f.slice(1)}</th>)}</tr></thead>
          <tbody>
            {features.map((f) => (
              <tr key={f} data-au-erow={f}>
                <td>{featName(f)}<small className="au-code">{f}</small></td>
                <td>{[...(used[f] || [])].filter(Boolean).map((x) => x[0].toUpperCase() + x.slice(1)).join(', ') || '—'}</td>
                {FAMS.map((fam) => {
                  // only the lines this feature can run on (routed in code, used in the period, or changed here) — the rest stay empty
                  const base = routes[f]?.[fam];
                  const mine = own[f]?.[fam] || '';
                  if (!base && !mine && !used[f]?.has(fam)) return <td key={fam} className="au-na">—</td>;
                  const lv = mine || base || modelDefault[fam];
                  return (
                    <td key={fam} className={mine ? 'au-changed' : ''}>
                      <select value={mine} onChange={(e) => setEffort(f, fam, e.target.value)} data-au-effort-sel={`${f}:${fam}`}
                        className={`au-pill lv-${lv}`} aria-label={`${featName(f)} · ${fam}`}>
                        <option value="">{base ? lvName(base) : t('xAiUse.eModelDefault', { l: lvName(modelDefault[fam]) })}</option>
                        {levels.map((l) => <option key={l} value={l}>{lvName(l)}</option>)}
                      </select>
                      {mine && <small className="au-tag">{t('xAiUse.eChanged')}</small>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4>{t('xAiUse.pricesTitle')}</h4>
      <p className="au-hint">{t('xAiUse.pricesHint')}</p>
      <div className="au-scroll">
        <table className="sp-table au-table" data-au-prices>
          <thead><tr>
            <th>{t('xAiUse.pModel')}</th><th>{t('xAiUse.pIn')}</th><th>{t('xAiUse.pOut')}</th><th>{t('xAiUse.pLongFrom')}</th>
            <th>{t('xAiUse.pInLong')}</th><th>{t('xAiUse.pOutLong')}</th><th>{t('xAiUse.pNote')}</th><th />
          </tr></thead>
          <tbody>
            {prices.map((p, i) => {
              const set = (k) => (e) => setPrices(prices.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
              return (
                <tr key={p.model}>
                  <td>{modelName(p.model)}<small className="au-code">{p.model}</small></td>
                  {['usdIn', 'usdOut', 'longFrom', 'usdInLong', 'usdOutLong'].map((k) => (
                    <td key={k}><input type="number" min="0" step={k === 'longFrom' ? 1000 : 0.01} value={p[k] ?? ''} onChange={set(k)} className="au-num" /></td>
                  ))}
                  <td><input type="text" value={p.note || ''} onChange={set('note')} maxLength={400} /></td>
                  <td><button type="button" className="sp-btn" onClick={() => savePrice(p)}>{t('xAiUse.save')}</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4>{t('xAiUse.keysTitle')}</h4>
      <ul className="au-keys">
        <li>{t('xAiUse.keyUser')}: <b>{data.keys.user ? t('xAiUse.keyYes') : t('xAiUse.keyNo')}</b></li>
        <li>{t('xAiUse.keySystem')}: <b>{data.keys.system ? t('xAiUse.keyYes') : t('xAiUse.keyNo')}</b></li>
        <li>{t('xAiUse.keyShared')}: <b>{data.keys.shared ? t('xAiUse.keyYes') : '—'}</b></li>
      </ul>
    </details>
  );
}

export default function AiUsage() {
  const { t } = useTranslation();
  const [env, setEnv] = useState('production');
  const [period, setPeriod] = useState('month');
  const [range, setRange] = useState(() => periodOf('month'));
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [f, setF] = useState({ entity: '', dept: '', person: '', feature: '', model: '' });
  const [group, setGroup] = useState('person');
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const h = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(h); }, []);

  const load = useCallback(() => axios.get(API, { params: { from: range[0], to: range[1], env } })
    .then((r) => { setData(r.data); setErr(''); }).catch((e) => setErr(errOf(e))), [range, env]);
  useEffect(() => { load(); }, [load]);
  const pickPeriod = (p) => { setPeriod(p); if (p !== 'custom') setRange(periodOf(p)); };

  const featName = useCallback((x) => t(`xAiUse.feat_${String(x).replace(/\./g, '_')}`, { defaultValue: x }), [t]);
  const acct = useCallback((r) => (r.kind === 'system' || !r.userId ? null : data?.users?.[r.userId] || null), [data]);
  const personName = useCallback((r) => (r.kind === 'system' || !r.userId ? t('xAiUse.system') : acct(r)?.name || `#${r.userId}`), [acct, t]);
  const keyOf = useMemo(() => ({
    person: (r) => personName(r),
    entity: (r) => (r.kind === 'system' ? t('xAiUse.system') : acct(r)?.entity || t('xAiUse.unknown')),
    dept: (r) => (r.kind === 'system' ? t('xAiUse.system') : acct(r)?.department || t('xAiUse.unknown')),
    feature: (r) => featName(r.feature),
    model: (r) => modelName(r.model),
    day: (r) => String(r.day).slice(0, 10),
  }), [personName, acct, featName, t]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const options = useMemo(() => {
    const o = {};
    Object.keys(f).forEach((k) => { o[k] = [...new Set(rows.map(keyOf[k === 'dept' ? 'dept' : k]))].sort((a, b) => a.localeCompare(b)); });
    return o;
  }, [rows, keyOf, f]);
  const shown = useMemo(() => rows.filter((r) => Object.entries(f).every(([k, v]) => !v || keyOf[k](r) === v)), [rows, f, keyOf]);
  const grouped = useMemo(() => {
    const m = new Map();
    shown.forEach((r) => {
      const k = keyOf[group](r);
      const g = m.get(k) || { name: k, calls: 0, tin: 0, tout: 0, usd: 0 };
      g.calls += r.calls; g.tin += r.tin; g.tout += r.tout; g.usd += r.usd;
      m.set(k, g);
    });
    // the most expensive first until a header is clicked
    return [...m.values()].map((g) => ({ ...g, name: group === 'day' ? dmy(g.name) : g.name })).sort((a, b) => b.usd - a.usd);
  }, [shown, group, keyOf]);
  // the daily chart: every day of the period, people and SYSTEM apart, the people's cap as a line
  const trend = useMemo(() => {
    if (!data) return [];
    const by = {};
    shown.forEach((r) => { const d = String(r.day).slice(0, 10); by[d] = by[d] || { people: 0, system: 0 }; by[d][r.kind === 'system' ? 'system' : 'people'] += r.usd; });
    const out = [];
    for (let d = range[0]; d <= range[1] && out.length < 400; d = addDays(d, 1)) out.push({ day: dmy(d), people: +(by[d]?.people || 0).toFixed(3), system: +(by[d]?.system || 0).toFixed(3) });
    return out;
  }, [shown, range, data]);
  /* SORT AND FILTER ON EVERY HEADER (Max 03/11), the portal's own funnels (useColumnTools) — a column sorts and filters on what
     its cell shows */
  const people = useMemo(() => (data?.people || []).map((p) => ({ ...p, cap: (p.own ?? (data.caps.person || 0)) + (p.extra || 0) })), [data]);
  const [capFor, setCapFor] = useState(null);   // { id, name } while the cap dialog is open
  const deepText = useCallback((p) => (p.admin ? t('xAiUse.deepAdmin', { n: p.deep || 0 }) : t('xAiUse.deepUsed', { n: p.deep || 0, limit: data?.caps?.opus })), [t, data]);
  const pTools = useColumnTools(people, useMemo(() => [
    { key: 'name', get: (p) => p.name }, { key: 'entity', get: (p) => p.entity || '—' },
    { key: 'dept', get: (p) => (p.departments || []).join(', ') || '—' }, { key: 'usd', get: (p) => p.usd },
    { key: 'cap', get: (p) => pct(p.usd, p.cap) }, { key: 'calls', get: (p) => p.calls }, { key: 'deep', get: (p) => deepText(p) },
  ], [deepText]));
  const gTools = useColumnTools(grouped, useMemo(() => [
    { key: 'name', get: (g) => g.name }, { key: 'calls', get: (g) => g.calls }, { key: 'tin', get: (g) => g.tin },
    { key: 'tout', get: (g) => g.tout }, { key: 'usd', get: (g) => g.usd },
  ], []));
  const gShown = gTools.shown;
  const gTotal = gShown.reduce((a, g) => a + g.usd, 0);
  const left = data ? Math.max(0, new Date(data.today.resetAt).getTime() - now) : 0;
  const anyFilter = Object.values(f).some(Boolean);
  const vsLast = data && data.month.lastSame > 0 ? `${data.month.thisMonth >= data.month.lastSame ? '+' : ''}${Math.round(pct(data.month.thisMonth - data.month.lastSame, data.month.lastSame))}%` : '—';

  return (
    <div className="pa-page">
      <Header />
      <main className="pa-main au-page">
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('xAiUse.title')}</h2>
            <p>{t('xAiUse.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="aiUsage" ready={!!data} />
          <div className="au-envbar">
            <select value={env} onChange={(e) => setEnv(e.target.value)} data-au-env aria-label="server">
              {['production', 'staging', 'all'].map((x) => <option key={x} value={x}>{t(`xAiUse.env_${x}`)}</option>)}
            </select>
            <button type="button" className="sp-btn" onClick={load}><RefreshCw size={13} /></button>
          </div>
        </div>
        {err && <p className="sp-error">{err}</p>}
        {!data ? (!err && <p className="sp-sub">…</p>) : (
          <>
            <section className="au-today" data-au-today>
              <div className="au-today-meters">
                <h3>{t('xAiUse.todayTitle')}</h3>
                <Meter label={t('xAiUse.peopleCap')} spent={data.today.users} cap={data.caps.users} testId="users" />
                <Meter label={t('xAiUse.systemCap')} spent={data.today.system} cap={data.caps.system} testId="system" />
                <p className="au-hint">{t('xAiUse.resetIn', { h: Math.floor(left / 3600000), m: Math.floor((left % 3600000) / 60000) })} · {t('xAiUse.stopsAt')}</p>
              </div>
              <div className="au-cards">
                <div className="au-card"><span>{t('xAiUse.cardMonth')}</span><b>{usd(data.month.thisMonth)}</b><small>{t('xAiUse.cardVsLast', { v: vsLast })}</small></div>
                <div className="au-card"><span>{t('xAiUse.p_last')}</span><b>{usd(data.month.lastMonth)}</b></div>
                <div className="au-card"><span>{t('xAiUse.cardQuestions')}</span><b>{int(data.month.questions)}</b><small>{t('xAiUse.cardQVs', { v: int(data.month.lastQuestions) })}</small></div>
                <div className="au-card"><span>{t('xAiUse.cardToday')}</span><b>{int(data.today.calls)}</b><small>{usd(data.today.total)}</small></div>
              </div>
            </section>

            <section data-au-people>
              <h3>{t('xAiUse.personToday')}</h3>
              {!people.length ? <p className="sp-sub">{t('xAiUse.noneToday')}</p> : (
                <div className="au-scroll">
                  {pTools.active && <button type="button" className="sp-btn au-clear" onClick={pTools.clear}>{t('xAiUse.clear')}</button>}
                  <table className="sp-table au-table" data-au-ptable>
                    <thead><tr>
                      <SortTh label={t('xAiUse.colPerson')} {...pTools.thProps('name')} />
                      <SortTh label={t('xAiUse.colEntity')} {...pTools.thProps('entity')} />
                      <SortTh label={t('xAiUse.colDept')} {...pTools.thProps('dept')} />
                      <SortTh label={t('xAiUse.colToday')} className="num" {...pTools.thProps('usd')} />
                      <SortTh label={t('xAiUse.colOwnCap')} {...pTools.thProps('cap')} />
                      <SortTh label={t('xAiUse.colCalls')} className="num" {...pTools.thProps('calls')} />
                      <SortTh label={t('xAiUse.colDeep')} {...pTools.thProps('deep')} />
                      <th>{t('xAiUse.colAction')}</th>
                    </tr></thead>
                    <tbody>
                      {pTools.shown.map((p) => {
                        const pp = pct(p.usd, p.cap);
                        return (
                          <tr key={p.userId} data-au-person={p.userId}>
                            <td title={p.fullName || ''}>{p.name}</td><td title={p.entityName || ''}>{p.entity || '—'}</td><td>{(p.departments || []).join(', ') || '—'}</td>
                            <td className="num">{usd(p.usd)}</td>
                            <td><div className="au-bar small" title={`${Math.round(pp)}% · ${usd(p.cap)}`}><div className={`au-fill ${level(pp)}`} style={{ width: `${Math.min(100, pp)}%` }} /></div>
                              {p.own ? <small className="au-tag au-owntag" data-au-own>{t('xAiUse.ownTag')} · {usd(p.own)}</small> : null}
                              {p.extra > 0 && <small className="au-tag" data-au-extra>{t('xAiUse.extraToday', { v: Number(p.extra).toFixed(2), cap: p.cap.toFixed(2) })}</small>}</td>
                            <td className="num">{int(p.calls)}</td>
                            <td>{deepText(p)}</td>
                            <td><button type="button" className="sp-btn au-act-edit" onClick={() => setCapFor({ id: p.userId, name: p.fullName || p.name })} data-au-setcap={p.userId}>{t('xAiUse.capSet')}</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="au-filters" data-au-filters>
              <label><span>{t('xAiUse.period')}</span>
                <select value={period} onChange={(e) => pickPeriod(e.target.value)} data-au-period>
                  {['today', 'week', 'month', 'last', 'custom'].map((p) => <option key={p} value={p}>{t(`xAiUse.p_${p}`)}</option>)}
                </select>
              </label>
              {period === 'custom' && (
                <>
                  <label><span>{t('xAiUse.dfrom')}</span><input type="date" value={range[0]} max={range[1]} onChange={(e) => e.target.value && setRange([e.target.value, range[1]])} data-au-from /></label>
                  <label><span>{t('xAiUse.dto')}</span><input type="date" value={range[1]} min={range[0]} onChange={(e) => e.target.value && setRange([range[0], e.target.value])} data-au-to /></label>
                </>
              )}
              {[['entity', 'fEntity'], ['dept', 'fDept'], ['person', 'fPerson'], ['feature', 'fFeature'], ['model', 'fModel']].map(([k, label]) => (
                <label key={k}><span>{t(`xAiUse.${label}`)}</span>
                  <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-au-f={k}>
                    <option value="">{t('xAiUse.all')}</option>
                    {options[k].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
              {anyFilter && <button type="button" className="sp-btn" onClick={() => setF({ entity: '', dept: '', person: '', feature: '', model: '' })}>{t('xAiUse.clear')}</button>}
            </section>

            <section className="au-chart">
              <h3>{t('xAiUse.trendTitle')}</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={trend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} width={44} />
                  <Tooltip formatter={(v) => usd(v, 3)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="people" stackId="a" name={t('xAiUse.peopleCap')} fill="#2563eb" />
                  <Bar dataKey="system" stackId="a" name={t('xAiUse.systemCap')} fill="#94a3b8" />
                  <ReferenceLine y={data.caps.users} stroke="#dc2626" strokeDasharray="5 4" label={{ value: t('xAiUse.capRef'), position: 'insideTopRight', fontSize: 11, fill: '#dc2626' }} />
                </BarChart>
              </ResponsiveContainer>
            </section>

            <section className="au-detail">
              <div className="au-grouprow">
                <label><span>{t('xAiUse.groupBy')}</span>
                  <select value={group} onChange={(e) => { setGroup(e.target.value); gTools.clear(); }} data-au-group>
                    {['person', 'entity', 'dept', 'feature', 'model', 'day'].map((g) => <option key={g} value={g}>{t(`xAiUse.g_${g}`)}</option>)}
                  </select>
                </label>
                {gTools.active && <button type="button" className="sp-btn" onClick={gTools.clear}>{t('xAiUse.clear')}</button>}
                {data.testRooms?.length > 0 && <span className="au-hint au-testnote">{t('xAiUse.testNote', { list: data.testRooms.map((x) => x.replace(/^GCC · /, '')).join(', ') })}</span>}
              </div>
              {!grouped.length ? <p className="sp-sub">{t('xAiUse.noRows')}</p> : (
                <div className="au-scroll">
                  <table className="sp-table au-table" data-au-grouped>
                    <thead><tr>
                      <SortTh label={t(`xAiUse.g_${group}`)} {...gTools.thProps('name')} />
                      <SortTh label={t('xAiUse.colCalls')} className="num" {...gTools.thProps('calls')} />
                      <SortTh label={t('xAiUse.colTin')} className="num" {...gTools.thProps('tin')} />
                      <SortTh label={t('xAiUse.colTout')} className="num" {...gTools.thProps('tout')} />
                      <SortTh label={t('xAiUse.colUsd')} className="num" {...gTools.thProps('usd')} />
                      <th className="num">{t('xAiUse.colShare')}</th>
                    </tr></thead>
                    <tbody>
                      {gShown.map((g) => (
                        <tr key={g.name}>
                          <td>{g.name}</td><td className="num">{int(g.calls)}</td><td className="num">{int(g.tin)}</td>
                          <td className="num">{int(g.tout)}</td><td className="num">{usd(g.usd, 3)}</td><td className="num">{gTotal > 0 ? `${pct(g.usd, gTotal).toFixed(1)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr>
                      <td>{t('xAiUse.total')}</td><td className="num">{int(gShown.reduce((a, g) => a + g.calls, 0))}</td>
                      <td className="num">{int(gShown.reduce((a, g) => a + g.tin, 0))}</td><td className="num">{int(gShown.reduce((a, g) => a + g.tout, 0))}</td>
                      <td className="num">{usd(gTotal, 3)}</td><td />
                    </tr></tfoot>
                  </table>
                </div>
              )}
            </section>

            <Settings data={data} reload={load} onCap={(a) => setCapFor({ id: a.id, name: a.name })} />
            {capFor && <CapDialog who={capFor} data={data} onClose={() => setCapFor(null)} onSaved={() => { setCapFor(null); load(); }} />}
          </>
        )}
      </main>
    </div>
  );
}
